/**
 * ItemImportExportModal
 *
 * Wizard-style modal for importing items from Miro, Jira, Excel/CSV, whiteboard
 * photo, or JSON paste; and exporting items to the same destinations.
 *
 * Canonical entry: ItemListView → Import. Whiteboard flow is owned here (not
 * wired from legacy Items.tsx). See `.cursor/rules/items-list-canonical.mdc`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * MIRO — STOP. DO NOT REGRESS SPATIAL / TEMPLATE-ZONE IMPORT.
 * Agents keep overwriting this with flat sticky-only import. That is a defect.
 * Default path MUST remain Template Mode → previewMiroTemplateImport →
 * importFromMiroTemplate ("Import with Zones"). Simple Mode is optional only.
 * Rule: `.cursor/rules/miro-spatial-import-do-not-regress.mdc`
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  X,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileSpreadsheet,
  FileDown,
  AlertCircle,
  Check,
  Upload,
  Loader2,
  Eye,
  Braces,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { parseCSV, generateCSV } from '../../../lib/connectors';
import { registerImportedRecords, type ImportedRecordIdentity } from '../../../lib/semanticConnectors/importIdentity';
import { importNotionDatabaseSemantic } from '../../../lib/semanticConnectors/importNotionSemantic';
import { importLinearTeamSemantic } from '../../../lib/semanticConnectors/importLinearSemantic';
import {
  linearProjects,
  linearStatus,
  linearTeams,
  type LinearProjectSummary,
  type LinearTeamSummary,
} from '../../../lib/semanticConnectors/linearSemanticApi';
import { importJiraProjectSemantic } from '../../../lib/semanticConnectors/importJiraSemantic';
import {
  getIntegrationsApi,
  resolveIntegrationsWorkspaceId,
  type MiroBoard,
  type JiraProject,
  type MiroTemplate,
  type MiroTemplateImportPreview,
  type NotionDatabaseSummary,
} from '../../../lib/integrationsApi';
import { miroBoardNotice } from '../../../lib/miroBoardNotice';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import type { Candidate, ItemLanguageType, ItemMetaCategory, StrategicBucket } from '../../../types/prioritization/lifecycle';
import type { AltitudeLevel } from '../../../types/prioritization/itemLifecycle';
import type { RelationshipEdgeType, RelationshipGroupType } from '../../../types/prioritization/itemRelationships';
import {
  resolveConnectorConfig,
  saveUserConnectorCredentials,
  saveWorkspaceConnectorCredentials,
  type ConnectorType,
} from '../../../lib/connectorConfigsService';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { canUseAnthropicVision } from '../../../lib/aiProviderKeys';
import { loadBoards } from '../../../lib/whiteboardBaselineStore';
import { WhiteboardImportModal } from './WhiteboardImportModal';
import { ExcelImportModal } from '../import-export/ExcelImportModal';
import { itemsImportConfig } from '../../../lib/importConfigs';
import {
  ItemImportSourceChooser,
  JiraIcon,
  MiroIcon,
  type ItemImportSource,
} from './ItemImportSourceChooser';
import { WORK_TYPE_IMPORT_HINT } from '../../../types/work-type-recipes';
import {
  buildPriorImportIndex,
  externalIdsAlreadyImported,
  newImportBatchId,
  summarizeImportOutcome,
  type ImportRowFailure,
} from '../../../lib/importBatch';
import { loadImportedRecordMappings } from '../../../lib/semanticConnectors/importIdentity';
import { ImportBatchActions } from './ImportBatchActions';
import {
  miroStickyColorsForExport,
  prepareMiroImportMapping,
  type MappingImportSummary,
} from '../../../lib/miroImportMapping';
import { MappingSetNotice } from '../connectors/MappingSetNotice';
import { Callout } from '@/components/ds';
import { DEFAULT_SEQUENCE_STRENGTH, isSequenceEdgeType, isSequenceStrength } from '../../../lib/sequenceRelationships';

// ============================================================================
// Constants
// ============================================================================

/** Columns users fill in when importing items. Only item-level fields; no ranking/list context. */
const IMPORT_COLUMNS = [
  'title',
  'description',
  'itemCategory',
  'metaCategory',
  'strategicBucket',
  'altitudeLevel',
  'tags',
  'source',
  'desiredOutcome',
  'beneficiary',
  'evidenceOfSuccess',
  'whyNow',
  'obstacles',
  'work_type',
  'launch_type',
  'investment_type',
  'exit_status',
  'parentItemId',
  'external_id',
] as const;

/** Extract a Miro board id from a board URL or bare id. */
function parseMiroBoardId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const fromUrl = trimmed.match(/miro\.com\/(?:app\/)?board\/([^/?#]+)/i);
  if (fromUrl?.[1]) {
    return decodeURIComponent(fromUrl[1].replace(/\/+$/, ''));
  }
  if (/^[A-Za-z0-9=_-]+$/.test(trimmed) && trimmed.length >= 8) return trimmed;
  return null;
}

/** Columns descriptions for the template instructions sheet. */
const COLUMN_DOCS: Record<string, { required: boolean; hint: string }> = {
  title:               { required: true,  hint: 'Short name of the item. Required.' },
  description:         { required: false, hint: 'Longer context or detail.' },
  itemCategory:        { required: false, hint: 'problem | opportunity | risk | solution | commitment | investment' },
  metaCategory:        { required: false, hint: 'people | things | places | activities | information | time' },
  strategicBucket:     { required: false, hint: 'growth | commitments | reliability | platform | compliance' },
  altitudeLevel:       { required: false, hint: '1 (Org-wide) through 5 (Task). Scope of the item.' },
  tags:                { required: false, hint: 'Comma-separated labels, e.g. "backend, Q3, urgent".' },
  source:              { required: false, hint: 'Where the item came from, e.g. "customer request".' },
  desiredOutcome:      { required: false, hint: 'What we hope to achieve.' },
  beneficiary:         { required: false, hint: 'Who benefits — a customer segment, team, or user type.' },
  evidenceOfSuccess:   { required: false, hint: 'How will we know this worked?' },
  whyNow:              { required: false, hint: 'Why is this the right time to pursue this?' },
  obstacles:           { required: false, hint: 'Known blockers or risks.' },
  work_type:           { required: false, hint: WORK_TYPE_IMPORT_HINT },
  launch_type:         { required: false, hint: 'Workspace launch type slug (Settings → Launch Types).' },
  investment_type:     { required: false, hint: 'Workspace investment type slug (Settings → Investment Types).' },
  exit_status:         { required: false, hint: 'active (default) | deferred | archived' },
  parentItemId:        { required: false, hint: 'ID of the parent item if this is a child.' },
  external_id:         { required: false, hint: 'Stable upstream key. Re-import upserts instead of duplicating.' },
};

/** Full export columns (all meaningful item fields, including read-only). */
const EXPORT_COLUMNS = [
  'id',
  'external_id',
  'title',
  'description',
  'lifecycle_state',
  'exit_status',
  'itemCategory',
  'metaCategory',
  'strategicBucket',
  'altitudeLevel',
  'tags',
  'source',
  'desiredOutcome',
  'beneficiary',
  'evidenceOfSuccess',
  'whyNow',
  'obstacles',
  'work_type',
  'launch_type',
  'investment_type',
  'parentItemId',
  'queueId',
  'createdBy',
  'createdAt',
  'updatedAt',
] as const;

/**
 * Columns for importing item relationships (edges and groups).
 * A row is an edge if it has relationship_type + source_item_id + target_item_id.
 * A row is a group if it has group_type + items (comma-separated item IDs, 2+).
 */
const RELATIONSHIP_COLUMNS = [
  'relationship_type',
  'source_item_id',
  'target_item_id',
  'group_type',
  'items',
  'title',
  'subtype',
  'rationale',
] as const;

const RELATIONSHIP_COLUMN_DOCS: Record<string, { required: boolean; hint: string }> = {
  relationship_type: { required: false, hint: 'Edge row: related | dependency | prerequisite | aggregation | elaboration | breakdown | contains | reframe' },
  source_item_id:    { required: false, hint: 'Edge row — the item ID this relationship points from.' },
  target_item_id:    { required: false, hint: 'Edge row — the item ID this relationship points to.' },
  group_type:        { required: false, hint: 'Group row: cluster | package | competing | collection | related_group' },
  items:             { required: false, hint: 'Group row — comma-separated item IDs (2 or more).' },
  title:             { required: false, hint: 'Group row — a name for the cluster/package/collection.' },
  subtype:           { required: false, hint: 'Dependency and prerequisite rows: the strength — informational | soft | hard | gate (soft when left blank). Cluster rows: duplicate | variant.' },
  rationale:         { required: false, hint: 'Why this relationship exists.' },
};

const VALID_EDGE_TYPES = new Set([
  'related', 'dependency', 'prerequisite', 'aggregation', 'elaboration', 'breakdown', 'contains', 'reframe',
]);

/** Accepts the product label "competing" as an alias for the DB value "exclusive". */
const GROUP_TYPE_ALIASES: Record<string, RelationshipGroupType> = {
  cluster: 'cluster',
  package: 'package',
  competing: 'exclusive',
  exclusive: 'exclusive',
  collection: 'collection',
  related_group: 'related_group',
};

/**
 * Columns for importing item provenance / lineage actions.
 * Each row needs an `action` plus source/target item ID(s), whose shape
 * depends on the action (see PROVENANCE_COLUMN_DOCS).
 */
const PROVENANCE_COLUMNS = [
  'action',
  'source_item_id',
  'source_item_ids',
  'target_item_id',
  'target_item_ids',
  'rationale',
] as const;

const PROVENANCE_COLUMN_DOCS: Record<string, { required: boolean; hint: string }> = {
  action:          { required: true,  hint: 'retire | supersede | split | merge | fork' },
  source_item_id:  { required: false, hint: 'Single source item ID — retire, supersede, split, fork.' },
  source_item_ids: { required: false, hint: 'Comma-separated source item IDs — merge (needs 2+).' },
  target_item_id:  { required: false, hint: 'Single target item ID — supersede, fork.' },
  target_item_ids: { required: false, hint: 'Comma-separated target item IDs — split (needs 2+) or merge.' },
  rationale:       { required: false, hint: 'Why this action happened.' },
};

const VALID_PROVENANCE_ACTIONS = new Set(['retire', 'supersede', 'split', 'merge', 'fork']);

// ============================================================================
// Types
// ============================================================================

type ModalMode = 'import' | 'export';
type Connector = 'excel-csv' | 'jira' | 'miro' | 'json' | 'notion' | 'linear';
type WizardStep = 'select-connector' | 'configure' | 'preview' | 'done';
/** Excel/CSV import can target three different entities; each has its own columns and target store. */
type ImportEntityType = 'items' | 'relationships' | 'provenance';

interface ParsedImportRow {
  title: string;
  description?: string;
  tags?: string[];
  source?: string;
  itemCategory?: string;
  metaCategory?: string;
  strategicBucket?: string;
  altitudeLevel?: number;
  desiredOutcome?: string;
  beneficiary?: string;
  evidenceOfSuccess?: string;
  whyNow?: string;
  obstacles?: string;
  work_type?: string;
  launch_type?: string;
  investment_type?: string;
  exit_status?: string;
  parentItemId?: string;
  external_id?: string;
}

interface ParsedRelationshipRow {
  isGroup: boolean;
  // Edge fields
  relationshipType?: string;
  sourceItemId?: string;
  targetItemId?: string;
  // Group fields
  groupType?: RelationshipGroupType;
  items?: string[];
  title?: string;
  // Shared
  subtype?: string;
  rationale?: string;
}

interface ParsedProvenanceRow {
  action: 'retire' | 'supersede' | 'split' | 'merge' | 'fork';
  sourceItemIds: string[];
  targetItemIds: string[];
  rationale?: string;
}

// ============================================================================
// Utility
// ============================================================================

function itemToExportRow(item: Candidate): Record<string, string> {
  const row: Record<string, string> = {};
  const itemRecord = item as unknown as Record<string, unknown>;
  for (const col of EXPORT_COLUMNS) {
    const val = itemRecord[col];
    if (val == null) { row[col] = ''; continue; }
    if (Array.isArray(val)) { row[col] = val.join(', '); continue; }
    if (val instanceof Date) { row[col] = val.toISOString(); continue; }
    if (typeof val === 'object') { row[col] = JSON.stringify(val); continue; }
    row[col] = String(val);
  }
  return row;
}

function parseRow(raw: Record<string, string>): ParsedImportRow | null {
  const title = raw['title']?.trim();
  if (!title) return null;
  const tagsRaw = raw['tags'] ?? '';
  const tags = tagsRaw ? tagsRaw.split(',').map((t) => t.trim()).filter(Boolean) : undefined;
  const altRaw = raw['altitudeLevel'];
  const altitudeLevel = altRaw ? parseInt(altRaw, 10) || undefined : undefined;
  return {
    title,
    description:       raw['description']?.trim() || undefined,
    tags:              tags?.length ? tags : undefined,
    source:            raw['source']?.trim() || undefined,
    itemCategory:      raw['itemCategory']?.trim() || undefined,
    metaCategory:      raw['metaCategory']?.trim() || undefined,
    strategicBucket:   raw['strategicBucket']?.trim() || undefined,
    altitudeLevel,
    desiredOutcome:    raw['desiredOutcome']?.trim() || undefined,
    beneficiary:       raw['beneficiary']?.trim() || undefined,
    evidenceOfSuccess: raw['evidenceOfSuccess']?.trim() || undefined,
    whyNow:            raw['whyNow']?.trim() || undefined,
    obstacles:         raw['obstacles']?.trim() || undefined,
    work_type:         raw['work_type']?.trim() || undefined,
    launch_type:       raw['launch_type']?.trim() || undefined,
    investment_type:   raw['investment_type']?.trim() || undefined,
    exit_status:       raw['exit_status']?.trim() || undefined,
    parentItemId:      raw['parentItemId']?.trim() || undefined,
    external_id:       raw['external_id']?.trim() || undefined,
  };
}

function splitIdList(raw?: string): string[] {
  return raw ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];
}

/** A row is a group (group_type + items[]) or an edge (relationship_type + source/target). */
function parseRelationshipRow(raw: Record<string, string>): ParsedRelationshipRow | null {
  const groupTypeRaw = raw['group_type']?.trim().toLowerCase();
  if (groupTypeRaw) {
    const groupType = GROUP_TYPE_ALIASES[groupTypeRaw];
    const items = splitIdList(raw['items']);
    if (!groupType || items.length < 2) return null;
    return {
      isGroup: true,
      groupType,
      items,
      title: raw['title']?.trim() || undefined,
      subtype: raw['subtype']?.trim() || undefined,
      rationale: raw['rationale']?.trim() || undefined,
    };
  }

  const relationshipType = raw['relationship_type']?.trim().toLowerCase();
  const sourceItemId = raw['source_item_id']?.trim();
  const targetItemId = raw['target_item_id']?.trim();
  if (!relationshipType || !VALID_EDGE_TYPES.has(relationshipType) || !sourceItemId || !targetItemId) return null;
  let subtype = raw['subtype']?.trim() || undefined;
  if (isSequenceEdgeType(relationshipType)) {
    // A dependency or prerequisite row states its strength (canon 12 §5.4; the
    // database refuses one without, migration 1004). Blank is the floor, soft; a
    // word outside the four makes the row invalid rather than a silent guess.
    if (subtype === undefined) subtype = DEFAULT_SEQUENCE_STRENGTH;
    else if (!isSequenceStrength(subtype.toLowerCase())) return null;
    else subtype = subtype.toLowerCase();
  }
  return {
    isGroup: false,
    relationshipType,
    sourceItemId,
    targetItemId,
    subtype,
    rationale: raw['rationale']?.trim() || undefined,
  };
}

function parseProvenanceRow(raw: Record<string, string>): ParsedProvenanceRow | null {
  const action = raw['action']?.trim().toLowerCase();
  if (!action || !VALID_PROVENANCE_ACTIONS.has(action)) return null;

  const sourceItemIds = splitIdList(raw['source_item_ids']);
  const singleSource = raw['source_item_id']?.trim();
  const finalSourceIds = sourceItemIds.length > 0 ? sourceItemIds : singleSource ? [singleSource] : [];

  const targetItemIds = splitIdList(raw['target_item_ids']);
  const singleTarget = raw['target_item_id']?.trim();
  const finalTargetIds = targetItemIds.length > 0 ? targetItemIds : singleTarget ? [singleTarget] : [];

  if (finalSourceIds.length === 0) return null;
  return {
    action: action as ParsedProvenanceRow['action'],
    sourceItemIds: finalSourceIds,
    targetItemIds: finalTargetIds,
    rationale: raw['rationale']?.trim() || undefined,
  };
}

// ============================================================================
// Main Component
// ============================================================================

function ExportConnectorCard({
  icon,
  label,
  description,
  onClick,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 border border-[var(--rule)] text-left transition-colors hover:border-primary-400 hover:bg-primary-50/40"
    >
      <div className="flex-shrink-0 w-10 h-10 bg-[var(--hover-tint)] flex items-center justify-center text-[var(--text-sec)]">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[var(--text-pri)]">{label}</span>
          {badge && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)] font-medium">
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">{description}</p>
      </div>
      <span className="text-[var(--text-ter)]" aria-hidden="true">›</span>
    </button>
  );
}

interface ItemImportExportModalProps {
  mode: ModalMode;
  onClose: () => void;
  items: Candidate[];
  /** A spreadsheet dropped on the parent page; opens directly in column mapping. */
  initialFile?: File | null;
  /**
   * Invoked when the user picks "Whiteboard Photo" (import only). The parent
   * closes this modal and opens WhiteboardImportModal, which has its own
   * upload → analyze → preview flow.
   */
  onUseWhiteboard?: () => void;
  initialSource?: ItemImportSource;
}

/** Shown on an import button the signed-in person may not use. */
const IMPORT_NEEDS_ITEMS_CREATE = 'You need permission to create items to import them.';

export function ItemImportExportModal({ mode, onClose, items, onUseWhiteboard, initialSource, initialFile }: ItemImportExportModalProps) {
  const createItemWithReason = useItemsStore((s) => s.createItemWithReason);
  const createLineageEvent = useItemsStore((s) => s.createLineageEvent);
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const createGroup = useItemRelationshipsStore((s) => s.createGroup);
  const dissolveEdge = useItemRelationshipsStore((s) => s.dissolveEdge);
  const dissolveGroup = useItemRelationshipsStore((s) => s.dissolveGroup);
  const addNotification = useUIStore((s) => s.addNotification);
  const { userId } = useUserStore();
  // Import creates items: the same items.create check the server applies to
  // every row (751 items_insert_with_permission), read before the click.
  const can = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const importBlocked = mode === 'import' && !can('items.create');
  const importBlockedTitle = importBlocked ? IMPORT_NEEDS_ITEMS_CREATE : undefined;

  const [whiteboardOpen, setWhiteboardOpen] = useState(
    mode === 'import' && (initialSource === 'whiteboard' || initialSource === 'whiteboard-recapture')
  );
  const [excelMapperOpen, setExcelMapperOpen] = useState(mode === 'import' && !!initialFile);
  const [mapperFile, setMapperFile] = useState<File | null>(initialFile ?? null);
  const [whiteboardMode, setWhiteboardMode] = useState<'new' | 'recapture'>(
    initialSource === 'whiteboard-recapture' ? 'recapture' : 'new'
  );
  const [hasSavedBoards, setHasSavedBoards] = useState(false);
  const hasAnthropicKey = canUseAnthropicVision();

  useEffect(() => {
    if (mode !== 'import') return;
    let active = true;
    loadBoards().then((b) => { if (active) setHasSavedBoards(b.length > 0); });
    return () => { active = false; };
  }, [mode]);

  useEffect(() => {
    if (mode === 'import' && initialFile) {
      setMapperFile(initialFile);
      setExcelMapperOpen(true);
    }
  }, [initialFile, mode]);

  const initialConnector =
    initialSource && !initialSource.startsWith('whiteboard')
      ? initialSource as Connector
      : null;
  const [step, setStep] = useState<WizardStep>(initialConnector ? 'configure' : 'select-connector');
  const [connector, setConnector] = useState<Connector | null>(initialConnector);

  // API availability — must use the real workspace UUID (not a hard-coded
  // sentinel like "items") so credentials match Settings → Connectors / DB.
  const [apiAvailable, setApiAvailable] = useState<boolean | null>(null);
  const workspaceIdFromStore = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceId = resolveIntegrationsWorkspaceId(
    workspaceIdFromStore,
    getCurrentWorkspaceId(),
  ) ?? 'default';
  const api = useMemo(() => getIntegrationsApi(workspaceId), [workspaceId]);

  // Miro state
  const [miroConnected, setMiroConnected] = useState(false);
  const [miroAutoConnecting, setMiroAutoConnecting] = useState(true);
  const [miroToken, setMiroToken] = useState('');
  const [miroBoards, setMiroBoards] = useState<MiroBoard[]>([]);
  const [miroTemplates, setMiroTemplates] = useState<MiroTemplate[]>([]);
  const [selectedMiroBoard, setSelectedMiroBoard] = useState('');
  const [miroBoardManual, setMiroBoardManual] = useState('');
  const [selectedMiroTemplate, setSelectedMiroTemplate] = useState('');
  const [miroConnecting, setMiroConnecting] = useState(false);
  /** Template Mode = spatial zone → metadata (default). Simple = flat sticky import. */
  const [useMiroTemplateMode, setUseMiroTemplateMode] = useState(true);
  const [miroTemplatePreview, setMiroTemplatePreview] = useState<MiroTemplateImportPreview | null>(null);
  const [miroTemplatePreviewLoading, setMiroTemplatePreviewLoading] = useState(false);
  // Names the board about to be read and counts its notes; warns when no
  // template is drawn on it (the zones would be a guess).
  const miroPreviewNotice = useMemo(
    () =>
      miroTemplatePreview
        ? miroBoardNotice({
            boardName: miroBoards.find((b) => b.id === selectedMiroBoard)?.name,
            willImport: miroTemplatePreview.totalItems,
            templateName: miroTemplatePreview.templateName,
            board: miroTemplatePreview.board,
          })
        : null,
    [miroTemplatePreview, miroBoards, selectedMiroBoard],
  );

  // Jira state
  const [jiraConnected, setJiraConnected] = useState(false);
  const [jiraSiteUrl, setJiraSiteUrl] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraToken, setJiraToken] = useState('');
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [selectedJiraProject, setSelectedJiraProject] = useState('');
  const [jiraConnecting, setJiraConnecting] = useState(false);

  // Notion (OAuth is shipped elsewhere — we only check status + list databases)
  const [notionConnected, setNotionConnected] = useState(false);
  const [notionChecking, setNotionChecking] = useState(false);
  const [notionDatabases, setNotionDatabases] = useState<NotionDatabaseSummary[]>([]);
  const [selectedNotionDb, setSelectedNotionDb] = useState('');

  // Linear (OAuth in Settings — status + team picker only)
  const [linearConnected, setLinearConnected] = useState(false);
  const [linearChecking, setLinearChecking] = useState(false);
  const [linearTeamList, setLinearTeamList] = useState<LinearTeamSummary[]>([]);
  const [selectedLinearTeam, setSelectedLinearTeam] = useState('');
  const [linearProjectList, setLinearProjectList] = useState<LinearProjectSummary[]>([]);
  const [selectedLinearProject, setSelectedLinearProject] = useState('');

  // Excel/CSV state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [entityType, setEntityType] = useState<ImportEntityType>('items');
  const [parsedRows, setParsedRows] = useState<ParsedImportRow[]>([]);
  const [parsedRelRows, setParsedRelRows] = useState<ParsedRelationshipRow[]>([]);
  const [parsedProvRows, setParsedProvRows] = useState<ParsedProvenanceRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  // Row count for the entity type currently selected — used for header/footer/preview text.
  const previewCount =
    entityType === 'items' ? parsedRows.length :
    entityType === 'relationships' ? parsedRelRows.length :
    parsedProvRows.length;

  // JSON paste state
  const [jsonText, setJsonText] = useState('');
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);

  // In-progress / done
  const [processing, setProcessing] = useState(false);
  const [resultCount, setResultCount] = useState(0);
  // The import run just completed (items.import_batch_id): drives "View these
  // N items" and "Undo this import" on the done screen.
  const [importBatch, setImportBatch] = useState<{
    id: string;
    itemIds: string[];
    skipped: number;
    edgeIds?: string[];
    collectionId?: string;
  } | null>(null);
  // Rows that did not land, with the reason. When non-empty the done screen is
  // a warning that lists them — never a green check over a partial import.
  const [importFailures, setImportFailures] = useState<Array<{ rowNumber: number; title: string; reason: string }>>([]);
  // What the Field Mappings set did on the last Miro import (done screen).
  const [mappingSummary, setMappingSummary] = useState<MappingImportSummary | null>(null);
  // Set while a mapped (Excel mapper) import has failures to show, so the
  // mapper's own close does not also close this modal before they are seen.
  const showResultsAfterMapperRef = useRef(false);
  const miroPreviewRequestRef = useRef(0);
  const navigate = useNavigate();

  // Load stored credentials from connector_configs (§2.1) + check API health
  useEffect(() => {
    api.checkHealth().then(() => setApiAvailable(true)).catch(() => setApiAvailable(false));

    if (!workspaceId || workspaceId === 'default') return;

    // Auto-connect Miro from connector_configs (§2.1 — no localStorage fallback)
    setMiroAutoConnecting(true);
    resolveConnectorConfig(workspaceId, userId ?? null, 'miro' as ConnectorType).then(async ({ data: cfg }) => {
      const token = (cfg?.authData as Record<string, string> | undefined)?.accessToken ?? '';
      if (!token) { setMiroAutoConnecting(false); return; }
      setMiroToken(token);
      try {
        try {
          const [boards, templates] = await Promise.all([api.listMiroBoards(), api.listMiroTemplates()]);
          setMiroBoards(boards);
          setMiroTemplates(templates);
          if (templates.length > 0) setSelectedMiroTemplate(templates[0].id);
          setMiroConnected(true);
        } catch {
          const result = await api.connectMiro(token);
          if (result.connected) {
            const [boards, templates] = await Promise.all([api.listMiroBoards(), api.listMiroTemplates()]);
            setMiroBoards(boards);
            setMiroTemplates(templates);
            if (templates.length > 0) setSelectedMiroTemplate(templates[0].id);
            setMiroConnected(true);
          }
        }
      } catch {
        // Miro auto-connect failed silently; user can reconnect manually
      } finally {
        setMiroAutoConnecting(false);
      }
    });

    // Auto-connect Jira from the resolved user/workspace connector config.
    resolveConnectorConfig(workspaceId, userId ?? null, 'jira' as ConnectorType).then(async ({ data: cfg }) => {
      const auth = cfg?.authData as Record<string, string> | undefined;
      if (!auth?.siteUrl || !auth?.email || !auth?.apiToken) return;
      setJiraSiteUrl(auth.siteUrl);
      setJiraEmail(auth.email);
      setJiraToken(auth.apiToken);
      setJiraConnecting(true);
      try {
        try {
          setJiraProjects(await api.listJiraProjects());
          setJiraConnected(true);
        } catch {
          const result = await api.connectJira(auth.siteUrl, auth.email, auth.apiToken);
          if (result.connected) {
            setJiraConnected(true);
            setJiraProjects(await api.listJiraProjects());
          }
        }
      } catch {
        // Keep the connector form available so the user can repair credentials.
      } finally {
        setJiraConnecting(false);
      }
    });

    // Notion: OAuth lives in Settings → Connectors (#218). Here we only check
    // whether it is connected and, if so, list databases for the picker.
    setNotionChecking(true);
    api.getNotionStatus(userId ?? undefined).then(async (status) => {
      if (status?.connected) {
        setNotionConnected(true);
        try {
          setNotionDatabases(await api.listNotionDatabases(userId ?? undefined));
        } catch {
          // leave the list empty; the user can retry
        }
      }
    }).catch(() => {
      // not connected / engine down — the config UI shows a connect link
    }).finally(() => setNotionChecking(false));

    // Linear: OAuth lives in Settings → Connectors (#217). Status + teams only.
    if (workspaceId && workspaceId !== 'default') {
      setLinearChecking(true);
      linearStatus(workspaceId).then(async (status) => {
        if (status.connected) {
          setLinearConnected(true);
          try {
            setLinearTeamList(await linearTeams(workspaceId));
          } catch {
            // leave empty; user can retry
          }
          try {
            setLinearProjectList(await linearProjects(workspaceId));
          } catch {
            // a team alone still imports
          }
        }
      }).catch(() => {
        // not connected / engine down
      }).finally(() => setLinearChecking(false));
    }
  }, [userId, api, workspaceId]);

  // ── Helpers ─────────────────────────────────────────────────────────────

  const fileName_export = `items_export_${new Date().toISOString().split('T')[0]}`;

  const exportRows = items.map(itemToExportRow);

  function downloadJson() {
    const data = items.map((item) => {
      const row: Record<string, unknown> = {};
      const itemRecord = item as unknown as Record<string, unknown>;
      for (const col of EXPORT_COLUMNS) {
        const val = itemRecord[col];
        if (val == null) continue;
        if (Array.isArray(val) && val.length === 0) continue;
        row[col] = val;
      }
      return row;
    });
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName_export}.json`;
    a.click();
    URL.revokeObjectURL(url);
    addNotification({ type: 'success', title: 'JSON exported', message: `Exported ${items.length} items.`, duration: 3000 });
    onClose();
  }

  function downloadCsv() {
    const fields = [...EXPORT_COLUMNS];
    const csv = generateCSV(exportRows, { fields: fields as string[], includeHeaders: true });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName_export}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    addNotification({ type: 'success', title: 'CSV exported', message: `Exported ${items.length} items.`, duration: 3000 });
    onClose();
  }

  function downloadExcel() {
    const ws = XLSX.utils.json_to_sheet(exportRows, { header: [...EXPORT_COLUMNS] as string[] });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, `${fileName_export}.xlsx`);
    addNotification({ type: 'success', title: 'Excel exported', message: `Exported ${items.length} items.`, duration: 3000 });
    onClose();
  }

  function downloadTemplateCsv() {
    const headers = [...IMPORT_COLUMNS] as string[];
    const exampleRow = Object.fromEntries(
      headers.map((h) => [h, h === 'title' ? 'Example initiative' : h === 'description' ? 'Brief description' : ''])
    );
    const csv = generateCSV([exampleRow], { fields: headers, includeHeaders: true });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'items_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadRelationshipsTemplateCsv() {
    const headers = [...RELATIONSHIP_COLUMNS] as string[];
    const rows = [
      {
        relationship_type: 'dependency', source_item_id: '<item-uuid-1>', target_item_id: '<item-uuid-2>',
        group_type: '', items: '', title: '', subtype: 'hard', rationale: 'Example: item 1 depends on item 2',
      },
      {
        relationship_type: '', source_item_id: '', target_item_id: '',
        group_type: 'cluster', items: '<item-uuid-3>,<item-uuid-4>', title: 'Example cluster', subtype: 'duplicate',
        rationale: 'Example: two items flagged as likely duplicates',
      },
    ];
    const csv = generateCSV(rows, { fields: headers, includeHeaders: true });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'item_relationships_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadProvenanceTemplateCsv() {
    const headers = [...PROVENANCE_COLUMNS] as string[];
    const rows = [
      { action: 'retire', source_item_id: '<item-uuid-1>', source_item_ids: '', target_item_id: '', target_item_ids: '', rationale: 'Example: item fully decommissioned' },
      { action: 'supersede', source_item_id: '<item-uuid-2>', source_item_ids: '', target_item_id: '<item-uuid-3>', target_item_ids: '', rationale: 'Example: item 2 replaced by item 3' },
      { action: 'split', source_item_id: '<item-uuid-4>', source_item_ids: '', target_item_id: '', target_item_ids: '<item-uuid-5>,<item-uuid-6>', rationale: 'Example: item 4 split into two items' },
      { action: 'merge', source_item_id: '', source_item_ids: '<item-uuid-7>,<item-uuid-8>', target_item_id: '<item-uuid-9>', target_item_ids: '', rationale: 'Example: two items merged into one' },
      { action: 'fork', source_item_id: '<item-uuid-10>', source_item_ids: '', target_item_id: '<item-uuid-11>', target_item_ids: '', rationale: 'Example: item 10 forked into a variant, source stays active' },
    ];
    const csv = generateCSV(rows, { fields: headers, includeHeaders: true });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'item_provenance_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadTemplateExcel() {
    const headers = [...IMPORT_COLUMNS] as string[];
    const exampleRow = Object.fromEntries(
      headers.map((h) => [h, h === 'title' ? 'Example initiative' : h === 'description' ? 'Brief description' : ''])
    );
    const ws = XLSX.utils.json_to_sheet([exampleRow], { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    const instructions: (string | number)[][] = [
      ['Items import template'],
      [],
      ['Only "title" is required. Leave all other columns blank if not applicable.'],
      [],
      ['Column', 'Required', 'Description'],
      ...headers.map((h) => {
        const doc = COLUMN_DOCS[h];
        return [h, doc?.required ? 'Yes' : 'No', doc?.hint ?? ''];
      }),
    ];
    const wsInstr = XLSX.utils.aoa_to_sheet(instructions);
    wsInstr['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');
    XLSX.writeFile(wb, 'items_import_template.xlsx');
  }

  function processSpecializedFile(file: File) {
    setParseError(null);
    setParsedRows([]);
    setParsedRelRows([]);
    setParsedProvRows([]);
    setFileName(file.name);

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const isCsv   = /\.csv$/i.test(file.name);
    if (!isExcel && !isCsv) {
      setParseError('Please choose a .csv, .xlsx, or .xls file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        let rows: Record<string, string>[] = [];
        if (isExcel) {
          const data = new Uint8Array(ev.target!.result as ArrayBuffer);
          const wb   = XLSX.read(data, { type: 'array' });
          const ws   = wb.Sheets[wb.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '' });
        } else {
          const text = ev.target!.result as string;
          const parsed = parseCSV(text, { hasHeaders: true });
          rows = parsed.rows as Record<string, string>[];
        }

        if (entityType === 'relationships') {
          const valid = rows.map(parseRelationshipRow).filter((r): r is ParsedRelationshipRow => r !== null);
          if (valid.length === 0) {
            setParseError('No valid rows found. Each row needs either (relationship_type, source_item_id, target_item_id) or (group_type, items).');
            return;
          }
          setParsedRelRows(valid);
          setStep('preview');
          return;
        }

        if (entityType === 'provenance') {
          const valid = rows.map(parseProvenanceRow).filter((r): r is ParsedProvenanceRow => r !== null);
          if (valid.length === 0) {
            setParseError('No valid rows found. Each row needs an "action" column (retire, supersede, split, merge, fork) and a source item ID.');
            return;
          }
          setParsedProvRows(valid);
          setStep('preview');
          return;
        }

        const valid = rows.map(parseRow).filter((r): r is ParsedImportRow => r !== null);
        if (valid.length === 0) {
          setParseError('No valid rows found. Make sure at least one row has a "title" column.');
          return;
        }
        setParsedRows(valid);
        setStep('preview');
      } catch (err) {
        setParseError(err instanceof Error ? err.message : 'Failed to parse file.');
      }
    };
    if (isExcel) reader.readAsArrayBuffer(file);
    else         reader.readAsText(file, 'UTF-8');
  }

  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (entityType === 'items') {
      setMapperFile(file);
      setExcelMapperOpen(true);
    } else {
      processSpecializedFile(file);
    }
    // reset so the same file can be re-selected
    e.target.value = '';
  }

  function handleFileDropped(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!/\.(csv|xlsx|xls)$/i.test(file.name)) {
      setParseError('Please choose a .csv, .xlsx, or .xls file.');
      return;
    }
    if (entityType === 'items') {
      setMapperFile(file);
      setExcelMapperOpen(true);
      return;
    }

    // Relationship and provenance files keep their specialized ID-aware parser.
    processSpecializedFile(file);
  }

  function handleJsonPaste() {
    setJsonParseError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText.trim());
    } catch {
      setJsonParseError('Invalid JSON — could not parse the text. Check for syntax errors.');
      return;
    }

    const rawItems = Array.isArray(parsed) ? parsed : [parsed];
    const rows: ParsedImportRow[] = [];
    for (const item of rawItems) {
      if (typeof item !== 'object' || item === null) continue;
      const coerced: Record<string, string> = {};
      for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
        if (Array.isArray(v)) {
          coerced[k] = v.join(', ');
        } else if (v == null) {
          coerced[k] = '';
        } else {
          coerced[k] = String(v);
        }
      }
      const row = parseRow(coerced);
      if (row) rows.push(row);
    }

    if (rows.length === 0) {
      setJsonParseError('No valid items found. Each object must have at least a "title" field.');
      return;
    }

    setParsedRows(rows);
    setFileName(null);
    setStep('preview');
  }

  async function confirmImport() {
    if (entityType === 'relationships') return confirmImportRelationships();
    if (entityType === 'provenance') return confirmImportProvenance();

    if (parsedRows.length === 0) return;
    setProcessing(true);
    const { applyCsvCitizenshipImport } = await import(
      '../../../lib/dataCitizenship/csvCitizenshipImport'
    );
    const rows = parsedRows.map((row) => ({
      title: row.title,
      description: row.description,
      tags: row.tags ?? [],
      source: row.source ?? (connector === 'json' ? 'JSON import' : 'Excel/CSV import'),
      ...(row.itemCategory && { itemCategory: row.itemCategory }),
      ...(row.metaCategory && { metaCategory: row.metaCategory }),
      ...(row.strategicBucket && { strategicBucket: row.strategicBucket }),
      ...(row.altitudeLevel && { altitudeLevel: row.altitudeLevel }),
      ...(row.desiredOutcome && { desiredOutcome: row.desiredOutcome }),
      ...(row.beneficiary && { beneficiary: row.beneficiary }),
      ...(row.evidenceOfSuccess && { evidenceOfSuccess: row.evidenceOfSuccess }),
      ...(row.whyNow && { whyNow: row.whyNow }),
      ...(row.obstacles && { obstacles: row.obstacles }),
      ...(row.work_type && { work_type: row.work_type }),
      ...(row.launch_type && { launch_type: row.launch_type }),
      ...(row.investment_type && { investment_type: row.investment_type }),
      ...(row.external_id && { external_id: row.external_id }),
      ...(row.parentItemId && { parentItemId: row.parentItemId }),
    }));
    const result = await applyCsvCitizenshipImport(rows, {
      profileName: connector === 'json' ? 'JSON import' : 'Template CSV/Excel',
      importBatchId: newImportBatchId(),
    });
    setImportBatch({ id: result.importBatchId, itemIds: result.createdItemIds, skipped: result.skippedAsImported });
    setResultCount(result.created + result.updated);
    setImportFailures(result.failures);
    setProcessing(false);
    setStep('done');
    notifyCsvImportResult(result);
  }

  function notifyCsvImportResult(result: {
    created: number; updated: number; clarified: number; failed: number; summaryLabel: string;
  }) {
    const landed = result.created + result.updated;
    const nothingLanded = result.failed > 0 && landed === 0;
    const notes = [
      result.failed > 0 ? 'The rows that failed are listed with the reason.' : null,
      result.clarified > 0 ? 'Review CSV rows in Settings → Conflict Queue.' : null,
    ].filter(Boolean).join(' ');
    addNotification({
      type: nothingLanded ? 'error' : result.failed > 0 || result.clarified > 0 ? 'warning' : 'success',
      title: nothingLanded
        ? 'Import failed'
        : result.failed > 0
          ? 'Import finished with errors'
          : 'Import complete',
      message: notes ? `${result.summaryLabel}. ${notes}` : result.summaryLabel,
      duration: result.failed > 0 ? 9000 : 6000,
    });
  }

  async function importMappedItems(
    rows: Record<string, unknown>[],
    meta?: { profileName?: string; profileId?: string },
  ) {
    const { applyCsvCitizenshipImport } = await import(
      '../../../lib/dataCitizenship/csvCitizenshipImport'
    );
    const result = await applyCsvCitizenshipImport(rows, {
      profileName: meta?.profileName,
      importBatchId: newImportBatchId(),
    });
    notifyCsvImportResult(result);
    setImportBatch({ id: result.importBatchId, itemIds: result.createdItemIds, skipped: result.skippedAsImported });
    if (result.failed > 0 || result.created > 0 || result.skippedAsImported > 0) {
      // Stay open on the results screen so the failed rows and reasons are
      // visible, and so "View these N items" / "Undo this import" are offered.
      showResultsAfterMapperRef.current = true;
      setEntityType('items');
      setResultCount(result.created + result.updated);
      setImportFailures(result.failures);
      setExcelMapperOpen(false);
      setMapperFile(null);
      setStep('done');
      return;
    }
    onClose();
  }

  async function confirmImportRelationships() {
    if (parsedRelRows.length === 0) return;
    setProcessing(true);
    let created = 0;
    const failures: Array<{ rowNumber: number; title: string; reason: string }> = [];
    for (const [index, row] of parsedRelRows.entries()) {
      const rowLabel = row.title || `${row.sourceItemId ?? '?'} → ${row.targetItemId ?? '?'}`;
      try {
        if (row.isGroup) {
          const result = await createGroup({
            type: row.groupType!,
            subtype: row.subtype as never,
            title: row.title,
            rationale: row.rationale,
            createdBy: userId ?? 'import',
            memberItemIds: row.items,
          });
          if (result) created++;
          else failures.push({ rowNumber: index + 1, title: rowLabel, reason: 'The group could not be saved' });
        } else {
          const result = await createEdge({
            type: row.relationshipType as RelationshipEdgeType,
            subtype: row.subtype as never,
            directionality: 'directed',
            status: 'active',
            declaredMode: 'declared',
            sourceItemId: row.sourceItemId!,
            targetItemId: row.targetItemId!,
            rationale: row.rationale,
            createdBy: userId ?? 'import',
            // createGroup fills workspaceId internally; createEdge does not, and the
            // RLS insert policy requires it — must be passed explicitly here.
            workspaceId: getCurrentWorkspaceId() ?? undefined,
          });
          if (result) created++;
          else failures.push({ rowNumber: index + 1, title: rowLabel, reason: 'The relationship could not be saved' });
        }
      } catch (err) {
        // continue on individual row errors, but remember them
        failures.push({ rowNumber: index + 1, title: rowLabel, reason: err instanceof Error ? err.message : 'The row could not be saved' });
      }
    }
    setResultCount(created);
    setImportFailures(failures);
    setProcessing(false);
    setStep('done');
    addNotification({
      type: failures.length === 0 ? 'success' : created === 0 ? 'error' : 'warning',
      title: failures.length === 0 ? 'Import complete' : created === 0 ? 'Import failed' : 'Import finished with errors',
      message: `${created} relationship${created !== 1 ? 's' : ''} created${failures.length ? `, ${failures.length} failed` : ''}.`,
      duration: failures.length ? 9000 : 4000,
    });
  }

  async function confirmImportProvenance() {
    if (parsedProvRows.length === 0) return;
    setProcessing(true);
    let created = 0;
    const failures: Array<{ rowNumber: number; title: string; reason: string }> = [];
    for (const [index, row] of parsedProvRows.entries()) {
      const rowLabel = `${row.action}: ${row.sourceItemIds.join(', ')} → ${row.targetItemIds.join(', ')}`;
      try {
        const result = await createLineageEvent({
          eventType: row.action,
          sourceItemIds: row.sourceItemIds,
          targetItemIds: row.targetItemIds,
          rationale: row.rationale,
        });
        if (result.success) created++;
        else failures.push({ rowNumber: index + 1, title: rowLabel, reason: result.error ?? 'The record could not be saved' });
      } catch (err) {
        // continue on individual row errors, but remember them
        failures.push({ rowNumber: index + 1, title: rowLabel, reason: err instanceof Error ? err.message : 'The record could not be saved' });
      }
    }
    setResultCount(created);
    setImportFailures(failures);
    setProcessing(false);
    setStep('done');
    addNotification({
      type: failures.length === 0 ? 'success' : created === 0 ? 'error' : 'warning',
      title: failures.length === 0 ? 'Import complete' : created === 0 ? 'Import failed' : 'Import finished with errors',
      message: `${created} provenance record${created !== 1 ? 's' : ''} created${failures.length ? `, ${failures.length} failed` : ''}.`,
      duration: failures.length ? 9000 : 4000,
    });
  }

  // Miro connect
  async function connectMiro() {
    if (!miroToken) return;
    setMiroConnecting(true);
    try {
      const result = await api.connectMiro(miroToken);
      if (!result.connected) {
        addNotification({
          type: 'error',
          title: 'Connection failed',
          message: result.error || 'Could not connect to Miro.',
          duration: 6000,
        });
        return;
      }
      if (workspaceId && workspaceId !== 'default' && userId) {
        await saveUserConnectorCredentials(workspaceId, userId, 'miro' as ConnectorType, {
          accessToken: miroToken,
        });
      }
      try {
        const [boards, templates] = await Promise.all([api.listMiroBoards(), api.listMiroTemplates()]);
        setMiroBoards(boards);
        setMiroTemplates(templates);
        if (templates.length > 0) setSelectedMiroTemplate(templates[0].id);
        setMiroConnected(true);
        if (boards.length === 0) {
          addNotification({
            type: 'info',
            title: 'Connected — no boards listed',
            message: 'Paste a Miro board URL below, or confirm the token’s team has boards and boards:read scope.',
            duration: 7000,
          });
        }
      } catch (listErr) {
        // Token validated, but listing failed (common on cold serverless instances
        // before credential DB fallback). Still mark connected and offer URL paste.
        setMiroConnected(true);
        setMiroBoards([]);
        addNotification({
          type: 'warning',
          title: 'Connected, but boards did not load',
          message: listErr instanceof Error ? listErr.message : 'Paste a Miro board URL to continue.',
          duration: 7000,
        });
      }
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Connection failed',
        message: err instanceof Error ? err.message : 'Could not reach Miro.',
        duration: 6000,
      });
    } finally {
      setMiroConnecting(false);
    }
  }

  function applyMiroBoardManual(raw: string) {
    setMiroBoardManual(raw);
    const id = parseMiroBoardId(raw);
    if (id) {
      setSelectedMiroBoard(id);
      if (useMiroTemplateMode && selectedMiroTemplate) {
        void loadMiroTemplatePreview(id, selectedMiroTemplate);
      }
    } else {
      setMiroTemplatePreview(null);
    }
  }

  async function loadMiroTemplatePreview(boardId: string, templateId: string) {
    // Only the latest request may answer: a slow preview of the board picked
    // first must not land on top of the board picked second.
    const request = ++miroPreviewRequestRef.current;
    if (!boardId || !templateId) {
      setMiroTemplatePreview(null);
      setMiroTemplatePreviewLoading(false);
      return;
    }
    setMiroTemplatePreview(null);
    setMiroTemplatePreviewLoading(true);
    try {
      const preview = await api.previewMiroTemplateImport(boardId, templateId, {
        filterToTemplateBounds: true,
        minConfidence: 'overlap',
        includeUnzoned: false,
      });
      if (request !== miroPreviewRequestRef.current) return;
      setMiroTemplatePreview(preview);
    } catch (err) {
      if (request !== miroPreviewRequestRef.current) return;
      setMiroTemplatePreview(null);
      addNotification({
        type: 'error',
        title: 'Preview failed',
        message: err instanceof Error ? err.message : 'Could not load zone preview.',
        duration: 4000,
      });
    } finally {
      if (request === miroPreviewRequestRef.current) setMiroTemplatePreviewLoading(false);
    }
  }

  // Jira connect — persists credentials to connector_configs (§2.1, §2.2)
  async function connectJira() {
    if (!jiraSiteUrl || !jiraEmail || !jiraToken) return;
    setJiraConnecting(true);
    try {
      const result = await api.connectJira(jiraSiteUrl, jiraEmail, jiraToken);
      if (result.connected) {
        setJiraConnected(true);
        // Persist to DB — connector_configs is the system of record (§2.1)
        const workspaceId = getCurrentWorkspaceId();
        if (workspaceId) {
          await saveWorkspaceConnectorCredentials(workspaceId, 'jira' as ConnectorType, {
            siteUrl: jiraSiteUrl,
            email: jiraEmail,
            apiToken: jiraToken,
          });
        }
        const projects = await api.listJiraProjects();
        setJiraProjects(projects);
      } else {
        addNotification({ type: 'error', title: 'Connection failed', message: 'Could not connect to Jira.', duration: 4000 });
      }
    } catch {
      addNotification({ type: 'error', title: 'Connection failed', message: 'Could not reach Jira.', duration: 4000 });
    } finally {
      setJiraConnecting(false);
    }
  }

  // ── Miro import ─────────────────────────────────────────────────────────
  // DO NOT delete Template Mode / importFromMiroTemplate. Flat importFromMiro
  // alone is a regression. See file header + miro-spatial-import-do-not-regress.

  /** Pull zone-derived fields from Miro template toolAttributes onto createItem. */
  function zoneFieldsFromToolAttributes(attrs: Record<string, unknown> | undefined): Partial<{
    itemCategory: ItemLanguageType;
    altitudeLevel: AltitudeLevel;
    metaCategory: ItemMetaCategory;
    strategicBucket: StrategicBucket;
    tags: string[];
  }> {
    if (!attrs) return {};
    const out: ReturnType<typeof zoneFieldsFromToolAttributes> = {};
    const cat = attrs.itemCategory ?? attrs.item_category ?? attrs.category;
    if (typeof cat === 'string' && cat.trim()) {
      out.itemCategory = cat as ItemLanguageType;
    }
    const alt = attrs.altitudeLevel ?? attrs.altitude_level ?? attrs.altitude;
    if (alt !== undefined && alt !== null && String(alt).trim() !== '') {
      out.altitudeLevel = String(alt) as AltitudeLevel;
    }
    const meta = attrs.metaCategory ?? attrs.meta_category;
    if (typeof meta === 'string' && meta.trim()) {
      out.metaCategory = meta as ItemMetaCategory;
    }
    const bucket = attrs.strategicBucket ?? attrs.strategic_bucket;
    if (typeof bucket === 'string' && bucket.trim()) {
      out.strategicBucket = bucket as StrategicBucket;
    }
    return out;
  }

  async function importFromMiro() {
    if (!selectedMiroBoard) return;
    if (useMiroTemplateMode && !selectedMiroTemplate) {
      addNotification({
        type: 'error',
        title: 'Template required',
        message: 'Pick a prioritization template to import with spatial zones.',
        duration: 4000,
      });
      return;
    }
    setProcessing(true);
    setImportFailures([]);
    setMappingSummary(null);
    try {
      const workspaceId = getCurrentWorkspaceId();
      // The Field Mappings set for Miro: item fields on create, attribute
      // values once each item exists (lib/miroImportMapping).
      const mapping = await prepareMiroImportMapping(workspaceId, {
        boardName: miroBoards.find((b) => b.id === selectedMiroBoard)?.name,
      });
      const identities: ImportedRecordIdentity[] = [];
      // A sticky counts only once the item store confirms the row was saved;
      // every refusal is kept with its reason for the done screen.
      const failures: ImportRowFailure[] = [];
      let created = 0;
      let skipped = 0;
      let zoneNote = '';
      const importBatchId = newImportBatchId();
      const createdItemIds: string[] = [];
      // Duplicate-skip on retry: a sticky whose Miro id already maps to an item
      // from an earlier, un-retired import batch is not created again.
      const priorIndex = buildPriorImportIndex(useItemsStore.getState().items);
      const skipSetFor = async (externalIds: string[]) =>
        workspaceId && priorIndex.itemIds.size > 0
          ? externalIdsAlreadyImported(
              priorIndex,
              await loadImportedRecordMappings({ workspaceId, connectorType: 'miro', externalIds }),
            )
          : new Set<string>();

      if (useMiroTemplateMode) {
        // Spatial import: sticky {x,y} → template zones → item metadata
        const result = await api.importFromMiroTemplate(selectedMiroBoard, selectedMiroTemplate, {
          filterToTemplateBounds: true,
          minConfidence: 'overlap',
          includeUnzoned: false,
        });
        if (!result.success) {
          addNotification({
            type: 'error',
            title: 'Import failed',
            message: result.warnings?.join(', ') || 'Miro template import failed.',
            duration: 5000,
          });
          return;
        }
        const alreadyImported = await skipSetFor((result.items ?? []).map((i) => i.id));
        for (const [index, item] of (result.items ?? []).entries()) {
          if (alreadyImported.has(item.id)) { skipped++; continue; }
          const content = item.content?.trim() || 'Untitled';
          const itemId = crypto.randomUUID();
          const zoneMeta = zoneFieldsFromToolAttributes(item.toolAttributes as Record<string, unknown>);
          const mapped = mapping.map(item);
          const saved = await createItemWithReason({
            id: itemId,
            title: content.substring(0, 100),
            description: content.length > 100 ? content : undefined,
            source: 'miro',
            tags: ['imported', 'miro', ...(item.zone?.name ? [`zone:${item.zone.name}`] : []), ...mapped.tags],
            importBatchId,
            // Template zones win over the set where both set the same field.
            ...mapped.itemFields,
            ...zoneMeta,
          } as Parameters<typeof createItemWithReason>[0]);
          if (!saved.ok) {
            failures.push({
              rowNumber: index + 1,
              title: content.substring(0, 100),
              reason: saved.reason ?? 'The item could not be saved',
            });
            continue;
          }
          createdItemIds.push(itemId);
          identities.push({
            internalId: itemId,
            externalId: item.id,
            label: content.substring(0, 100),
          });
          created++;
          await mapping.finish(itemId, mapped);
        }
        zoneNote = result.zoneSummary
          .filter((z) => z.itemCount > 0)
          .map((z) => `${z.zoneName}: ${z.itemCount}`)
          .join(', ');
      } else {
        // Simple flat sticky import (no zone geography)
        const result = await api.importFromMiro(selectedMiroBoard);
        const alreadyImported = await skipSetFor(
          (result.items ?? []).map((i) => i.externalId).filter((x): x is string => !!x),
        );
        for (const [index, item] of (result.items ?? []).entries()) {
          if (item.externalId && alreadyImported.has(item.externalId)) { skipped++; continue; }
          const itemId = crypto.randomUUID();
          // The engine sends the sticky text as `label` (cut to 100 characters).
          const title = item.title ?? item.label ?? 'Untitled';
          const mapped = mapping.map(item);
          const saved = await createItemWithReason({
            id: itemId,
            title,
            description: item.description,
            source: 'Miro import',
            tags: ['imported', 'miro', ...mapped.tags],
            importBatchId,
            ...mapped.itemFields,
          } as Parameters<typeof createItemWithReason>[0]);
          if (!saved.ok) {
            failures.push({
              rowNumber: index + 1,
              title,
              reason: saved.reason ?? 'The item could not be saved',
            });
            continue;
          }
          createdItemIds.push(itemId);
          if (item.externalId) {
            identities.push({ internalId: itemId, externalId: item.externalId, label: title });
          }
          created++;
          await mapping.finish(itemId, mapped);
        }
      }

      if (workspaceId && identities.length > 0) {
        await registerImportedRecords({
          workspaceId,
          entityType: 'item',
          connectorType: 'miro',
          records: identities,
        });
      }
      setResultCount(created);
      setImportBatch({ id: importBatchId, itemIds: createdItemIds, skipped });
      setImportFailures(failures);
      setMappingSummary(mapping.summary());
      setStep('done');
      addNotification(
        summarizeImportOutcome({
          created,
          failures,
          skipped,
          label: 'Miro import',
          successTitle: useMiroTemplateMode ? 'Miro zone import complete' : 'Miro import complete',
          note: zoneNote || undefined,
          failuresListed: true,
        }),
      );
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Import failed',
        message: err instanceof Error ? err.message : 'Failed to import from Miro.',
        duration: 5000,
      });
    } finally {
      setProcessing(false);
    }
  }

  // ── Jira semantic import ─────────────────────────────────────────────────
  // Set 6: a Jira project imports as items that carry relationships (issue
  // links → canonical edges) and hierarchy (epic/parent → contains), dropped by
  // the flat import, plus external identity — meaning, not records. Same shape
  // as importFromNotion / importFromLinear.
  async function importFromJira() {
    if (!selectedJiraProject) return;
    const wsId = getCurrentWorkspaceId();
    if (!wsId) {
      addNotification({ type: 'error', title: 'Import failed', message: 'No active workspace.', duration: 4000 });
      return;
    }
    setProcessing(true);
    try {
      const { data, error } = await importJiraProjectSemantic({
        workspaceId: wsId,
        projectKey: selectedJiraProject,
        userId: userId ?? undefined,
      });
      if (error || !data) {
        addNotification({ type: 'error', title: 'Import failed', message: error ?? 'Jira import failed.', duration: 5000 });
        return;
      }
      setResultCount(data.itemsCreated);
      setImportBatch({
        id: data.importBatchId,
        itemIds: data.createdItemIds,
        skipped: data.itemsSkipped,
        edgeIds: data.createdEdgeIds,
        collectionId: data.collectionId,
      });
      setStep('done');
      const edgeNote = data.edgesCreated > 0 || data.proposalsCreated > 0
        ? ` · ${data.edgesCreated} relationship${data.edgesCreated !== 1 ? 's' : ''}${data.proposalsCreated > 0 ? `, ${data.proposalsCreated} proposed for review` : ''}`
        : '';
      addNotification({
        type: 'success',
        title: 'Jira import complete',
        message: `${data.itemsCreated} issue${data.itemsCreated !== 1 ? 's' : ''} imported as items${edgeNote}.${data.itemsSkipped > 0 ? ` ${data.itemsSkipped} skipped as already imported.` : ''}`,
        duration: 5000,
      });
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Import failed',
        message: err instanceof Error ? err.message : 'Failed to import from Jira.',
        duration: 5000,
      });
    } finally {
      setProcessing(false);
    }
  }

  // ── Notion semantic import ───────────────────────────────────────────────
  // The whole point of set 4: a Notion database imports as items that carry
  // relationships (from relation properties, dropped by the flat import),
  // category (from option colors), and external identity — meaning, not records.
  async function importFromNotion() {
    if (!selectedNotionDb) return;
    const wsId = getCurrentWorkspaceId();
    if (!wsId) {
      addNotification({ type: 'error', title: 'Import failed', message: 'No active workspace.', duration: 4000 });
      return;
    }
    setProcessing(true);
    try {
      const { data, error } = await importNotionDatabaseSemantic({
        workspaceId: wsId,
        databaseId: selectedNotionDb,
        userId: userId ?? undefined,
      });
      if (error || !data) {
        addNotification({ type: 'error', title: 'Import failed', message: error ?? 'Notion import failed.', duration: 5000 });
        return;
      }
      setResultCount(data.itemsCreated);
      setImportBatch({
        id: data.importBatchId,
        itemIds: data.createdItemIds,
        skipped: data.itemsSkipped,
        edgeIds: data.createdEdgeIds,
        collectionId: data.collectionId,
      });
      setStep('done');
      const edgeNote = data.edgesCreated > 0 || data.proposalsCreated > 0
        ? ` · ${data.edgesCreated} relationship${data.edgesCreated !== 1 ? 's' : ''}${data.proposalsCreated > 0 ? `, ${data.proposalsCreated} proposed for review` : ''}`
        : '';
      addNotification({
        type: 'success',
        title: 'Notion import complete',
        message: `${data.itemsCreated} item${data.itemsCreated !== 1 ? 's' : ''} created${edgeNote}.${data.itemsSkipped > 0 ? ` ${data.itemsSkipped} skipped as already imported.` : ''}`,
        duration: 5000,
      });
    } catch (err) {
      addNotification({ type: 'error', title: 'Import failed', message: err instanceof Error ? err.message : 'Failed to import from Notion.', duration: 5000 });
    } finally {
      setProcessing(false);
    }
  }

  // ── Linear semantic import ───────────────────────────────────────────────
  async function importFromLinear() {
    if (!selectedLinearTeam && !selectedLinearProject) return;
    const wsId = getCurrentWorkspaceId();
    if (!wsId) {
      addNotification({ type: 'error', title: 'Import failed', message: 'No active workspace.', duration: 4000 });
      return;
    }
    setProcessing(true);
    try {
      const { data, error } = await importLinearTeamSemantic({
        workspaceId: wsId,
        teamId: selectedLinearTeam || undefined,
        projectId: selectedLinearProject || undefined,
        userId: userId ?? undefined,
      });
      if (error || !data) {
        addNotification({ type: 'error', title: 'Import failed', message: error ?? 'Linear import failed.', duration: 5000 });
        return;
      }
      setResultCount(data.itemsCreated);
      setImportBatch({
        id: data.importBatchId,
        itemIds: data.createdItemIds,
        skipped: data.itemsSkipped,
        edgeIds: data.createdEdgeIds,
        collectionId: data.collectionId,
      });
      setStep('done');
      const edgeNote = data.edgesCreated > 0 || data.proposalsCreated > 0
        ? ` · ${data.edgesCreated} relationship${data.edgesCreated !== 1 ? 's' : ''}${data.proposalsCreated > 0 ? `, ${data.proposalsCreated} proposed for review` : ''}`
        : '';
      addNotification({
        type: 'success',
        title: 'Linear import complete',
        message: `${data.itemsCreated} issue${data.itemsCreated !== 1 ? 's' : ''} imported as items${edgeNote}.${data.fieldsApplied > 0 ? ` Description and Priority came over on ${data.fieldsApplied}.` : ''}${data.itemsSkipped > 0 ? ` ${data.itemsSkipped} skipped as already imported.` : ''}`,
        duration: 5000,
      });
    } catch (err) {
      addNotification({ type: 'error', title: 'Import failed', message: err instanceof Error ? err.message : 'Failed to import from Linear.', duration: 5000 });
    } finally {
      setProcessing(false);
    }
  }

  // ── Jira export ─────────────────────────────────────────────────────────

  async function exportToJira() {
    if (!selectedJiraProject) return;
    setProcessing(true);
    try {
      const exportPayload = items.map((item) => ({
        id:          item.id,
        title:       item.title,
        rank:        0,
        description: item.description ?? '',
      }));
      const result = await api.exportToJira(selectedJiraProject, exportPayload, { includeDescription: true, includeRank: false });
      setResultCount(result.summary?.successful ?? 0);
      setStep('done');
      addNotification({ type: 'success', title: 'Jira export complete', message: `Created ${result.summary?.successful ?? 0} issue${(result.summary?.successful ?? 0) !== 1 ? 's' : ''} in Jira.`, duration: 4000 });
    } catch (err) {
      addNotification({ type: 'error', title: 'Export failed', message: err instanceof Error ? err.message : 'Failed to export to Jira.', duration: 5000 });
    } finally {
      setProcessing(false);
    }
  }

  // ── Miro export ─────────────────────────────────────────────────────────

  async function exportToMiro() {
    if (!selectedMiroTemplate) return;
    setProcessing(true);
    try {
      // The Miro export set, run backwards: an item's value picks its sticky color.
      const { colors } = await miroStickyColorsForExport(workspaceId !== 'default' ? workspaceId : null, items);
      const payload = items.map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        ...(colors.has(item.id) ? { color: colors.get(item.id) } : {}),
      }));
      await api.exportToMiroTemplate(selectedMiroTemplate, payload, { createBoard: true, newBoardName: 'Items Export' });
      setResultCount(items.length);
      setStep('done');
      const colorNote = colors.size > 0
        ? ` ${colors.size} sticky color${colors.size !== 1 ? 's' : ''} set from your Field Mappings set.`
        : '';
      addNotification({ type: 'success', title: 'Miro export complete', message: `${items.length} items sent to Miro.${colorNote}`, duration: 4000 });
    } catch (err) {
      addNotification({ type: 'error', title: 'Export failed', message: err instanceof Error ? err.message : 'Failed to export to Miro.', duration: 5000 });
    } finally {
      setProcessing(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const title = mode === 'import' ? 'Import items' : 'Export Items';
  const accentIcon = mode === 'import'
    ? <ArrowDownToLine className="h-5 w-5 text-primary-600" />
    : <ArrowUpFromLine className="h-5 w-5 text-primary-600" />;

  const openWhiteboard = (wbMode: 'new' | 'recapture' = 'new') => {
    // 'new' can defer to a parent-provided callback (legacy wiring); 're-capture'
    // is always self-contained.
    if (wbMode === 'new' && onUseWhiteboard) {
      onClose();
      onUseWhiteboard();
      return;
    }
    setWhiteboardMode(wbMode);
    setWhiteboardOpen(true);
  };

  if (whiteboardOpen) {
    return (
      <WhiteboardImportModal
        initialMode={whiteboardMode}
        returnToSourceChooser
        onClose={() => setWhiteboardOpen(false)}
        onImported={() => {
          setWhiteboardOpen(false);
          onClose();
        }}
      />
    );
  }

  if (excelMapperOpen) {
    return (
      <ExcelImportModal
        isOpen
        initialFile={mapperFile}
        onClose={() => {
          if (showResultsAfterMapperRef.current) {
            // Failed rows are waiting on the results screen — keep this modal open.
            showResultsAfterMapperRef.current = false;
            setExcelMapperOpen(false);
            setMapperFile(null);
            return;
          }
          if (initialFile) onClose();
          else {
            setExcelMapperOpen(false);
            setMapperFile(null);
          }
        }}
        onImport={importMappedItems}
        appFields={itemsImportConfig.appFields}
        title={itemsImportConfig.label}
        description={itemsImportConfig.description}
        validateRow={itemsImportConfig.validateRow}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50">{accentIcon}</div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-pri)]">{title}</h2>
              <p className="text-xs text-[var(--text-ter)]">
                {step === 'select-connector' && 'Choose a source or destination'}
                {step === 'configure'        && connector === 'excel-csv' && mode === 'import' && 'Upload a file to import'}
                {step === 'configure'        && connector === 'excel-csv' && mode === 'export' && 'Download your items'}
                {step === 'configure'        && connector === 'miro'      && (mode === 'import' ? 'Import sticky notes with spatial zone metadata' : 'Connect to Miro and choose a board')}
                {step === 'configure'        && connector === 'jira'      && (mode === 'import' ? 'Connect to Jira and select a project' : 'Connect to Jira and select a project')}
                {step === 'preview'          && `Preview — ${previewCount} valid row${previewCount !== 1 ? 's' : ''} found`}
                {step === 'done'             && 'All done'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* ── Step: Select connector ── */}
          {step === 'select-connector' && (
            <div className="space-y-3">
              {mode === 'import' ? (
                <ItemImportSourceChooser
                  hasAnthropicKey={hasAnthropicKey}
                  hasSavedBoards={hasSavedBoards}
                  onConnectConnector={(nextConnector) => {
                    setConnector(nextConnector);
                    setStep('configure');
                  }}
                  onSelect={(source) => {
                    if (source === 'whiteboard') {
                      openWhiteboard('new');
                    } else if (source === 'whiteboard-recapture') {
                      openWhiteboard('recapture');
                    } else {
                      setConnector(source);
                      if (source === 'json') {
                        setJsonText('');
                        setJsonParseError(null);
                      }
                      setStep('configure');
                    }
                  }}
                />
              ) : (
                <>
                  <ExportConnectorCard
                    icon={<FileSpreadsheet className="h-5 w-5" />}
                    label="Excel (.xlsx)"
                    description={`Download ${items.length} item${items.length !== 1 ? 's' : ''} as an Excel spreadsheet.`}
                    onClick={downloadExcel}
                  />
                  <ExportConnectorCard
                    icon={<FileDown className="h-5 w-5" />}
                    label="CSV"
                    description={`Download ${items.length} item${items.length !== 1 ? 's' : ''} as a comma-separated values file.`}
                    onClick={downloadCsv}
                  />
                  <ExportConnectorCard
                    icon={<Braces className="h-5 w-5" />}
                    label="JSON"
                    description={`Download ${items.length} item${items.length !== 1 ? 's' : ''} as a JSON file — useful for scripting or re-importing.`}
                    onClick={downloadJson}
                  />
                  <ExportConnectorCard
                    icon={<JiraIcon />}
                    label="Jira"
                    description="Create Jira issues from your items in a selected project."
                    badge={apiAvailable === false ? 'Engine required' : undefined}
                    onClick={() => { setConnector('jira'); setStep('configure'); }}
                  />
                  <ExportConnectorCard
                    icon={<MiroIcon />}
                    label="Miro"
                    description="Send items as sticky notes to a Miro board."
                    badge={apiAvailable === false ? 'Engine required' : undefined}
                    onClick={() => { setConnector('miro'); setStep('configure'); }}
                  />
                </>
              )}

              {apiAvailable === false && (
                <div className="flex items-start gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Miro and Jira require the integrations engine.
                    Run <code className="px-1.5 py-0.5 bg-[var(--mustard-dim)] rounded text-xs">npm run dev</code> inside{' '}
                    <code className="px-1.5 py-0.5 bg-[var(--mustard-dim)] rounded text-xs">engines/integrations-engine</code> to enable them.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Step: Configure — Excel/CSV import ── */}
          {step === 'configure' && connector === 'excel-csv' && mode === 'import' && (
            <div className="space-y-4">
              {/* Entity type selector */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">What are you importing?</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { id: 'items' as const,         label: 'Items' },
                    { id: 'relationships' as const, label: 'Relationships' },
                    { id: 'provenance' as const,    label: 'Provenance' },
                  ]).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { setEntityType(opt.id); setParseError(null); }}
                      className={`px-3 py-2  text-sm font-medium border transition-colors ${
                        entityType === opt.id
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[var(--text-ter)] mt-1.5">
                  {entityType === 'items' && 'Create new items in your library.'}
                  {entityType === 'relationships' && 'Connect existing items by ID — dependencies, prerequisites, clusters, packages, competing sets, collections.'}
                  {entityType === 'provenance' && 'Record how existing items changed identity over time — retired, superseded, split, merged, or forked. Writes to Item Lineage and History.'}
                </p>
              </div>

              {/* Template download */}
              <div className="p-4 bg-[var(--surface)] border border-[var(--rule)] space-y-2">
                <p className="text-sm font-medium text-[var(--text-pri)]">Start with a template</p>
                <p className="text-xs text-[var(--text-ter)]">
                  {entityType === 'items' && (
                    <>Download a template that includes all supported columns with descriptions.
                    Only <strong>title</strong> is required — fill in what you have and leave the rest blank.</>
                  )}
                  {entityType === 'relationships' && (
                    <>Download a template CSV with one example edge row and one example group row.
                    Item IDs must already exist in your workspace.</>
                  )}
                  {entityType === 'provenance' && (
                    <>Download a template CSV with one example row per action type.
                    Item IDs must already exist in your workspace.</>
                  )}
                </p>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={
                      entityType === 'items' ? downloadTemplateCsv :
                      entityType === 'relationships' ? downloadRelationshipsTemplateCsv :
                      downloadProvenanceTemplateCsv
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)]"
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    Template (CSV)
                  </button>
                  {entityType === 'items' && (
                    <button
                      type="button"
                      onClick={downloadTemplateExcel}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)]"
                    >
                      <FileDown className="h-3.5 w-3.5" />
                      Template (Excel)
                    </button>
                  )}
                </div>
              </div>

              {/* File upload */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleFileSelected}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDropped}
                  className="w-full flex flex-col items-center gap-2 py-8 border-2 border-dashed border-[var(--rule)] hover:border-primary-400 hover:bg-primary-50/30 transition-colors text-[var(--text-ter)] hover:text-primary-600"
                >
                  <Upload className="h-7 w-7" />
                  <span className="text-sm font-medium">Drop file here or click to upload</span>
                  <span className="text-xs">.csv, .xlsx, or .xls</span>
                </button>
              </div>

              {parseError && (
                <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {parseError}
                </div>
              )}

              {/* Column reference */}
              <details className="group">
                <summary className="cursor-pointer text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" />
                  View all supported columns
                </summary>
                <div className="mt-2 border overflow-hidden text-xs">
                  <table className="w-full">
                    <thead className="bg-[var(--surface)]">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-[var(--text-sec)]">Column</th>
                        <th className="px-3 py-2 text-left font-medium text-[var(--text-sec)]">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--rule)]">
                      {(
                        entityType === 'items' ? (IMPORT_COLUMNS as readonly string[]) :
                        entityType === 'relationships' ? (RELATIONSHIP_COLUMNS as readonly string[]) :
                        (PROVENANCE_COLUMNS as readonly string[])
                      ).map((col) => {
                        const docs =
                          entityType === 'items' ? COLUMN_DOCS :
                          entityType === 'relationships' ? RELATIONSHIP_COLUMN_DOCS :
                          PROVENANCE_COLUMN_DOCS;
                        return (
                          <tr key={col} className="hover:bg-[var(--surface)]">
                            <td className="px-3 py-1.5 font-mono text-[var(--text-pri)] whitespace-nowrap">
                              {col}
                              {docs[col]?.required && (
                                <span className="ml-1 text-[var(--coral)]">*</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-[var(--text-ter)]">{docs[col]?.hint}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}

          {/* ── Step: Configure — JSON paste ── */}
          {step === 'configure' && connector === 'json' && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--text-sec)]">
                Paste a JSON array of objects. Each object becomes one item.
                Only <strong>title</strong> is required — include any other fields you have.
              </p>

              <textarea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder={'[\n  { "title": "Improve onboarding", "description": "...", "tags": ["ux", "q3"] },\n  { "title": "Reduce load time" }\n]'}
                rows={10}
                spellCheck={false}
                className="w-full px-3 py-2 border border-[var(--rule)] text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
              />

              {jsonParseError && (
                <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {jsonParseError}
                </div>
              )}

              <details className="group">
                <summary className="cursor-pointer text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" />
                  View all supported fields
                </summary>
                <div className="mt-2 border overflow-hidden text-xs">
                  <table className="w-full">
                    <thead className="bg-[var(--surface)]">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-[var(--text-sec)]">Field</th>
                        <th className="px-3 py-2 text-left font-medium text-[var(--text-sec)]">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--rule)]">
                      {(IMPORT_COLUMNS as readonly string[]).map((col) => (
                        <tr key={col} className="hover:bg-[var(--surface)]">
                          <td className="px-3 py-1.5 font-mono text-[var(--text-pri)] whitespace-nowrap">
                            {col}
                            {COLUMN_DOCS[col]?.required && (
                              <span className="ml-1 text-[var(--coral)]">*</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-[var(--text-ter)]">{COLUMN_DOCS[col]?.hint}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}

          {/* ── Step: Configure — Miro ── */}
          {step === 'configure' && connector === 'miro' && (
            <div className="space-y-4">
              {apiAvailable === false && (
                <div className="flex items-start gap-3 p-3 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 border border-[var(--mustard)] dark:border-[var(--mustard)] text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Your Miro token is fine — the <strong>integrations engine</strong> is not reachable
                    (this app is configured for <code className="px-1 py-0.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 rounded text-sm font-mono">localhost:4100</code>).
                    Start it with{' '}
                    <code className="px-1.5 py-0.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 rounded text-sm font-mono">
                      cd engines/integrations-engine && npm run dev
                    </code>
                    , then click Connect again.
                  </span>
                </div>
              )}
              {miroAutoConnecting ? (
                <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting to Miro…
                </div>
              ) : !miroConnected ? (
                <>
                  <p className="text-sm text-[var(--text-sec)]">
                    Enter your Miro access token to connect. Generate one from your{' '}
                    <a href="https://miro.com/app/settings/user-profile/apps" target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
                      Miro developer settings
                    </a>.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Access Token <span className="text-[var(--coral)]">*</span>
                    </label>
                    <input
                      type="password"
                      value={miroToken}
                      onChange={(e) => setMiroToken(e.target.value)}
                      placeholder="Enter your Miro access token"
                      className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={connectMiro}
                    disabled={!miroToken || miroConnecting}
                    className="w-full btn btn-primary flex items-center justify-center gap-2"
                  >
                    {miroConnecting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {miroConnecting ? 'Connecting…' : 'Connect to Miro'}
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-3 py-2 border border-[var(--green)]">
                    <Check className="h-4 w-4" />
                    Connected to Miro
                  </div>
                  {mode === 'import' ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-4 p-3 bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)]">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={useMiroTemplateMode}
                            onChange={() => {
                              setUseMiroTemplateMode(true);
                              if (selectedMiroBoard && selectedMiroTemplate) {
                                void loadMiroTemplatePreview(selectedMiroBoard, selectedMiroTemplate);
                              }
                            }}
                            className="text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                            Template Mode
                          </span>
                          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                            (metadata from spatial zones)
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={!useMiroTemplateMode}
                            onChange={() => {
                              setUseMiroTemplateMode(false);
                              setMiroTemplatePreview(null);
                            }}
                            className="text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                            Simple Mode
                          </span>
                          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                            (flat sticky import)
                          </span>
                        </label>
                      </div>

                      {useMiroTemplateMode && (
                        <div>
                          <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1">
                            Prioritization template
                          </label>
                          <select
                            value={selectedMiroTemplate}
                            onChange={(e) => {
                              const templateId = e.target.value;
                              setSelectedMiroTemplate(templateId);
                              if (selectedMiroBoard && templateId) {
                                void loadMiroTemplatePreview(selectedMiroBoard, templateId);
                              } else {
                                setMiroTemplatePreview(null);
                              }
                            }}
                            className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] text-sm bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                          >
                            <option value="">-- Select a template --</option>
                            {miroTemplates.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.zoneCount} zones)
                              </option>
                            ))}
                          </select>
                          {selectedMiroTemplate && (
                            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">
                              {miroTemplates.find((t) => t.id === selectedMiroTemplate)?.description}
                            </p>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1">
                          Select board to import from
                        </label>
                        <select
                          value={miroBoards.some((b) => b.id === selectedMiroBoard) ? selectedMiroBoard : ''}
                          onChange={(e) => {
                            const boardId = e.target.value;
                            setSelectedMiroBoard(boardId);
                            setMiroBoardManual('');
                            if (useMiroTemplateMode && boardId && selectedMiroTemplate) {
                              void loadMiroTemplatePreview(boardId, selectedMiroTemplate);
                            } else {
                              setMiroTemplatePreview(null);
                            }
                          }}
                          className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] text-sm bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                        >
                          <option value="">-- Select a board --</option>
                          {miroBoards.map((b) => (
                            <option key={b.id} value={b.id}>{b.name || b.id}</option>
                          ))}
                        </select>
                        {miroBoards.length === 0 && (
                          <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mt-1">
                            No boards returned for this token’s team. Paste a board link below, or regenerate the
                            token with <code className="font-mono text-sm">boards:read</code> on the correct Miro team.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1">
                          Or paste board URL / ID
                        </label>
                        <input
                          type="text"
                          value={miroBoardManual}
                          onChange={(e) => applyMiroBoardManual(e.target.value)}
                          placeholder="https://miro.com/app/board/uXjV…=/"
                          className="w-full px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule)] text-sm bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                        />
                        {miroBoardManual && !parseMiroBoardId(miroBoardManual) && (
                          <p className="text-xs text-[var(--coral)] mt-1">Could not parse a board id from that value.</p>
                        )}
                      </div>

                      {useMiroTemplateMode ? (
                        <>
                          {(miroTemplatePreviewLoading || processing) && (
                            <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {miroTemplatePreviewLoading ? 'Detecting zones…' : 'Importing…'}
                            </div>
                          )}
                          {!miroTemplatePreviewLoading && miroTemplatePreview && miroPreviewNotice && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-[var(--text-pri)]">
                                {miroPreviewNotice.headline}
                              </p>
                              {miroPreviewNotice.warning && (
                                <div
                                  role="alert"
                                  className="flex items-start gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--text-pri)]"
                                >
                                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-[var(--mustard)]" />
                                  <span>{miroPreviewNotice.warning}</span>
                                </div>
                              )}
                              <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                                Zone distribution
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {miroTemplatePreview.zoneSummary.map((zone) => (
                                  <div
                                    key={zone.zoneId}
                                    className="p-2.5 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)]"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                                        {zone.zoneName}
                                      </span>
                                      <span className="text-sm font-bold text-primary-600">{zone.itemCount}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                                <strong>{miroTemplatePreview.totalItems}</strong> sticky notes will import with
                                zone-based metadata (altitude, category, etc.).
                              </p>
                              {miroTemplatePreview.sampleItems?.length > 0 && (
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-[var(--text-ter)]">Sample</p>
                                  {miroTemplatePreview.sampleItems.slice(0, 5).map((item) => (
                                    <div key={item.id} className="text-xs flex items-center gap-2">
                                      <span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 border border-primary-100">
                                        {item.zone}
                                      </span>
                                      <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate">
                                        {item.content}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {!miroTemplatePreviewLoading && selectedMiroBoard && !selectedMiroTemplate && (
                            <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                              Select a prioritization template to preview spatial zone detection.
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                          Sticky notes from the board will be imported as items (no zone metadata).
                        </p>
                      )}
                      <MappingSetNotice
                        connectorType="miro"
                        workspaceId={workspaceId !== 'default' ? workspaceId : null}
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Select template</label>
                      <select
                        value={selectedMiroTemplate}
                        onChange={(e) => setSelectedMiroTemplate(e.target.value)}
                        className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                      >
                        <option value="">-- Select a template --</option>
                        {miroTemplates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-[var(--text-ter)] mt-1">A new Miro board will be created from this template with your {items.length} items.</p>
                      <MappingSetNotice
                        connectorType="miro"
                        flow="export"
                        workspaceId={workspaceId !== 'default' ? workspaceId : null}
                        className="mt-2"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Step: Configure — Jira ── */}
          {step === 'configure' && connector === 'jira' && (
            <div className="space-y-4">
              {!jiraConnected ? (
                  <>
                    <p className="text-sm text-[var(--text-sec)]">
                      Enter your Jira credentials to connect. You need a site URL, email, and an{' '}
                      <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
                        API token
                      </a>.
                    </p>
                    {[
                      { label: 'Site URL',  value: jiraSiteUrl, set: setJiraSiteUrl, placeholder: 'https://yourcompany.atlassian.net', type: 'text' as const },
                      { label: 'Email',     value: jiraEmail,   set: setJiraEmail,   placeholder: 'you@company.com',                  type: 'email' as const },
                      { label: 'API Token', value: jiraToken,   set: setJiraToken,   placeholder: 'Jira API token',                   type: 'password' as const },
                    ].map(({ label, value, set, placeholder, type }) => (
                      <div key={label}>
                        <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">{label} <span className="text-[var(--coral)]">*</span></label>
                        <input
                          type={type}
                          value={value}
                          onChange={(e) => set(e.target.value)}
                          placeholder={placeholder}
                          className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={connectJira}
                      disabled={!jiraSiteUrl || !jiraEmail || !jiraToken || jiraConnecting}
                      className="w-full btn btn-primary flex items-center justify-center gap-2"
                    >
                      {jiraConnecting && <Loader2 className="h-4 w-4 animate-spin" />}
                      {jiraConnecting ? 'Connecting…' : 'Connect to Jira'}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-3 py-2 border border-[var(--green)]">
                      <Check className="h-4 w-4" />
                      Connected to Jira
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                        Select project to {mode === 'import' ? 'import from' : 'export to'}
                      </label>
                      <select
                        value={selectedJiraProject}
                        onChange={(e) => setSelectedJiraProject(e.target.value)}
                        className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                      >
                        <option value="">-- Select a project --</option>
                        {jiraProjects.map((p) => (
                          <option key={p.key} value={p.key}>{p.name} ({p.key})</option>
                        ))}
                      </select>
                      <p className="text-xs text-[var(--text-ter)] mt-1">
                        {mode === 'import'
                          ? 'Up to 100 issues from this project will be created as intake items.'
                          : `${items.length} items will be created as Jira issues in the selected project.`}
                      </p>
                    </div>
                  </>
                )
              }
            </div>
          )}

          {/* ── Step: Preview (Excel/CSV import) — Items ── */}
          {step === 'preview' && entityType === 'items' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-3 py-2 border border-[var(--green)]">
                <Check className="h-4 w-4" />
                <span>
                  <strong>{parsedRows.length}</strong> valid row{parsedRows.length !== 1 ? 's' : ''}
                  {fileName ? <> from <em>{fileName}</em></> : ' ready to import'}
                </span>
              </div>

              <div className="border overflow-hidden text-xs">
                <div className="bg-[var(--surface)] px-3 py-2 font-medium text-[var(--text-sec)]">Preview (first 5 rows)</div>
                <div className="divide-y divide-[var(--rule)] max-h-52 overflow-y-auto">
                  {parsedRows.slice(0, 5).map((row, i) => (
                    <div key={i} className="px-3 py-2 space-y-0.5">
                      <div className="font-medium text-[var(--text-pri)] truncate">{row.title}</div>
                      {row.description && (
                        <div className="text-[var(--text-ter)] truncate">{row.description}</div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {row.itemCategory && <span className="px-1.5 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">{row.itemCategory}</span>}
                        {row.altitudeLevel && <span className="px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">Alt {row.altitudeLevel}</span>}
                        {row.tags?.map((t) => (
                          <span key={t} className="px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">{t}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {parsedRows.length > 5 && (
                    <div className="px-3 py-2 text-[var(--text-ter)] italic">
                      +{parsedRows.length - 5} more row{parsedRows.length - 5 !== 1 ? 's' : ''}…
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-[var(--text-ter)]">
                Confirming will create {parsedRows.length} item{parsedRows.length !== 1 ? 's' : ''} in your items library.
                Existing items will not be affected.
              </p>
            </div>
          )}

          {/* ── Step: Preview (Excel/CSV import) — Relationships ── */}
          {step === 'preview' && entityType === 'relationships' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-3 py-2 border border-[var(--green)]">
                <Check className="h-4 w-4" />
                <span>
                  <strong>{parsedRelRows.length}</strong> valid row{parsedRelRows.length !== 1 ? 's' : ''}
                  {fileName ? <> from <em>{fileName}</em></> : ' ready to import'}
                </span>
              </div>

              <div className="border overflow-hidden text-xs">
                <div className="bg-[var(--surface)] px-3 py-2 font-medium text-[var(--text-sec)]">Preview (first 5 rows)</div>
                <div className="divide-y divide-[var(--rule)] max-h-52 overflow-y-auto">
                  {parsedRelRows.slice(0, 5).map((row, i) => (
                    <div key={i} className="px-3 py-2 space-y-0.5">
                      {row.isGroup ? (
                        <>
                          <div className="font-medium text-[var(--text-pri)] truncate">{row.title || `${row.groupType} group`}</div>
                          <div className="text-[var(--text-ter)] truncate">{row.items!.length} members</div>
                        </>
                      ) : (
                        <div className="font-medium text-[var(--text-pri)] truncate">
                          {row.sourceItemId} <span className="text-[var(--text-ter)]">→ {row.relationshipType} →</span> {row.targetItemId}
                        </div>
                      )}
                      {row.rationale && <div className="text-[var(--text-ter)] truncate">{row.rationale}</div>}
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="px-1.5 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
                          {row.isGroup ? row.groupType : row.relationshipType}
                        </span>
                        {row.subtype && <span className="px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">{row.subtype}</span>}
                      </div>
                    </div>
                  ))}
                  {parsedRelRows.length > 5 && (
                    <div className="px-3 py-2 text-[var(--text-ter)] italic">
                      +{parsedRelRows.length - 5} more row{parsedRelRows.length - 5 !== 1 ? 's' : ''}…
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-[var(--text-ter)]">
                Confirming will create {parsedRelRows.length} relationship{parsedRelRows.length !== 1 ? 's' : ''}.
                Item IDs that don't exist in this workspace will fail and be skipped; other rows still import.
              </p>
            </div>
          )}

          {/* ── Step: Preview (Excel/CSV import) — Provenance ── */}
          {step === 'preview' && entityType === 'provenance' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-3 py-2 border border-[var(--green)]">
                <Check className="h-4 w-4" />
                <span>
                  <strong>{parsedProvRows.length}</strong> valid row{parsedProvRows.length !== 1 ? 's' : ''}
                  {fileName ? <> from <em>{fileName}</em></> : ' ready to import'}
                </span>
              </div>

              <div className="border overflow-hidden text-xs">
                <div className="bg-[var(--surface)] px-3 py-2 font-medium text-[var(--text-sec)]">Preview (first 5 rows)</div>
                <div className="divide-y divide-[var(--rule)] max-h-52 overflow-y-auto">
                  {parsedProvRows.slice(0, 5).map((row, i) => (
                    <div key={i} className="px-3 py-2 space-y-0.5">
                      <div className="font-medium text-[var(--text-pri)] truncate">
                        <span className="px-1.5 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded mr-1.5">{row.action}</span>
                        {row.sourceItemIds.join(', ')}
                        {row.targetItemIds.length > 0 && <span className="text-[var(--text-ter)]"> → {row.targetItemIds.join(', ')}</span>}
                      </div>
                      {row.rationale && <div className="text-[var(--text-ter)] truncate">{row.rationale}</div>}
                    </div>
                  ))}
                  {parsedProvRows.length > 5 && (
                    <div className="px-3 py-2 text-[var(--text-ter)] italic">
                      +{parsedProvRows.length - 5} more row{parsedProvRows.length - 5 !== 1 ? 's' : ''}…
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-[var(--text-ter)]">
                Confirming will write {parsedProvRows.length} lineage record{parsedProvRows.length !== 1 ? 's' : ''}, visible in
                each item's History tab. Item IDs that don't exist in this workspace will fail and be skipped.
              </p>
            </div>
          )}

          {/* ── Step: Done ── */}
          {step === 'done' && (() => {
            const noun = entityType === 'items' ? 'item' : entityType === 'relationships' ? 'relationship' : 'provenance record';
            const hasFailures = mode === 'import' && importFailures.length > 0;
            const nothingLanded = hasFailures && resultCount === 0;
            return (
              <div className="flex flex-col items-center py-6 gap-3 text-center">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${hasFailures ? 'bg-[var(--mustard-dim)]' : 'bg-[var(--green-dim)]'}`}>
                  {hasFailures
                    ? <AlertCircle className="h-7 w-7 text-[var(--mustard)]" />
                    : <Check className="h-7 w-7 text-[var(--green)]" />}
                </div>
                <div>
                  <p className="text-base font-semibold text-[var(--text-pri)]">
                    {mode === 'import'
                      ? hasFailures
                        ? nothingLanded
                          ? `Nothing was imported — ${importFailures.length} row${importFailures.length !== 1 ? 's' : ''} failed`
                          : `${resultCount} ${noun}${resultCount !== 1 ? 's' : ''} imported, ${importFailures.length} row${importFailures.length !== 1 ? 's' : ''} failed`
                        : `${resultCount} ${noun}${resultCount !== 1 ? 's' : ''} imported`
                      : `${resultCount} item${resultCount !== 1 ? 's' : ''} exported`}
                  </p>
                  <p className="text-sm text-[var(--text-ter)] mt-1">
                    {mode === 'import'
                      ? hasFailures
                        ? 'The rows below were not saved. Fix them in your file and import those rows again.'
                        : (entityType === 'items' ? 'Items are now in your library.'
                          : entityType === 'relationships' ? 'Relationships are now visible on the connected items.'
                          : 'Lineage records are now visible in each item\u2019s History tab.')
                      : 'The operation completed successfully.'}
                  </p>
                </div>
                {mode === 'import' && entityType === 'items' && importBatch && (
                  <ImportBatchActions
                    key={importBatch.id}
                    batchId={importBatch.id}
                    itemIds={importBatch.itemIds}
                    skipped={importBatch.skipped}
                    onNavigate={onClose}
                    beforeUndo={async () => {
                      // Relationships and the collection this import wrote would
                      // block retire_new_item (canon 12 §599) — dissolve them first.
                      for (const edgeId of importBatch.edgeIds ?? []) {
                        try { await dissolveEdge(edgeId); } catch { /* continue */ }
                      }
                      if (importBatch.collectionId) {
                        try { await dissolveGroup(importBatch.collectionId); } catch { /* continue */ }
                      }
                    }}
                  />
                )}
                {mode === 'import' && connector === 'miro' && mappingSummary && (
                  <Callout
                    tone={mappingSummary.hasProblems ? 'mustard' : 'neutral'}
                    label="Field Mappings"
                    className="w-full text-left"
                  >
                    {mappingSummary.message}
                  </Callout>
                )}
                {hasFailures && (
                  <ul
                    className="w-full max-h-56 overflow-y-auto text-left border border-[var(--rule)] divide-y divide-[var(--rule)]"
                    aria-label="Rows that failed to import"
                  >
                    {importFailures.map((f) => (
                      <li key={`${f.rowNumber}-${f.title}`} className="px-3 py-2 text-sm">
                        <span className="font-medium text-[var(--text-pri)]">Row {f.rowNumber}: {f.title}</span>
                        <span className="block text-[var(--text-ter)]">{f.reason}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })()}

          {/* Notion — connect lives in Settings; here we pick a database */}
          {step === 'configure' && connector === 'notion' && mode === 'import' && (
            <div className="space-y-4">
              {notionChecking ? (
                <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking your Notion connection…
                </div>
              ) : !notionConnected ? (
                <div className="flex items-start gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Notion isn't connected.{' '}
                    <Link to="/prioritization/settings/connectors" className="underline hover:no-underline">
                      Connect it in Settings → Connectors
                    </Link>
                    , then reopen this import.
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-3 py-2 border border-[var(--green)]">
                    <Check className="h-4 w-4" /> Connected to Notion
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Select a database to import</label>
                    <select
                      value={selectedNotionDb}
                      onChange={(e) => setSelectedNotionDb(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Choose a database…</option>
                      {notionDatabases.map((db) => (
                        <option key={db.id} value={db.id}>{db.title || 'Untitled'}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-[var(--text-ter)]">
                    Relation properties import as canonical relationships; a colored status/select property
                    maps to item category. Inferred links come back as review proposals, not silent edges.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Linear — connect lives in Settings; here we pick a team */}
          {step === 'configure' && connector === 'linear' && mode === 'import' && (
            <div className="space-y-4">
              {linearChecking ? (
                <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking your Linear connection…
                </div>
              ) : !linearConnected ? (
                <div className="flex items-start gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Linear isn't connected.{' '}
                    <Link to="/prioritization/settings/connectors" className="underline hover:no-underline">
                      Connect it in Settings → Connectors
                    </Link>
                    , then reopen this import.
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-3 py-2 border border-[var(--green)]">
                    <Check className="h-4 w-4" /> Connected to Linear
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Project</label>
                    <select
                      value={selectedLinearProject}
                      onChange={(e) => setSelectedLinearProject(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      data-testid="linear-import-project"
                    >
                      <option value="">Any project — the whole team</option>
                      {linearProjectList.map((project) => (
                        <option key={project.id} value={project.id}>{project.name}{project.team?.name ? ` (${project.team.name})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Team{selectedLinearProject ? ' (optional)' : ''}</label>
                    <select
                      value={selectedLinearTeam}
                      onChange={(e) => setSelectedLinearTeam(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      data-testid="linear-import-team"
                    >
                      <option value="">{selectedLinearProject ? 'Every team in the project' : 'Choose a team…'}</option>
                      {linearTeamList.map((team) => (
                        <option key={team.id} value={team.id}>{team.name}{team.key ? ` (${team.key})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-[var(--text-ter)]">
                    Each issue becomes an item linked to it; its description and priority come over through the
                    Linear field mappings. Issue relations (blocks, related, sub-issues) import as canonical
                    relationships; the first label or workflow-state color maps to item category. Inferred links
                    come back as proposals.
                  </p>
                </>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        {step !== 'select-connector' && step !== 'done' && (
          <div className="px-6 py-4 border-t border-[var(--rule)] flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                if (step === 'preview') {
                  setStep('configure');
                  setParsedRows([]);
                  setParsedRelRows([]);
                  setParsedProvRows([]);
                  setFileName(null);
                } else {
                  setStep('select-connector');
                  setConnector(null);
                }
              }}
              className="btn btn-secondary text-sm"
            >
              {step === 'configure' && mode === 'import' ? 'Select another tool' : 'Back'}
            </button>

            {/* JSON parse */}
            {step === 'configure' && connector === 'json' && (
              <button
                type="button"
                onClick={handleJsonPaste}
                disabled={!jsonText.trim()}
                className="btn btn-primary flex items-center gap-2"
              >
                <Braces className="h-4 w-4" />
                Parse JSON
              </button>
            )}

            {/* Excel/CSV import confirm */}
            {step === 'preview' && (
              <button
                type="button"
                onClick={confirmImport}
                disabled={processing || previewCount === 0 || (entityType === 'items' && importBlocked)}
                title={entityType === 'items' ? importBlockedTitle : undefined}
                className="btn btn-primary flex items-center gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                {processing
                  ? 'Importing…'
                  : `Import ${previewCount} ${entityType === 'items' ? 'item' : entityType === 'relationships' ? 'relationship' : 'provenance record'}${previewCount !== 1 ? 's' : ''}`}
              </button>
            )}

            {/* Miro action */}
            {step === 'configure' && connector === 'miro' && miroConnected && mode === 'import' && (
              <button
                type="button"
                onClick={importFromMiro}
                title={importBlockedTitle}
                disabled={
                  importBlocked ||
                  !selectedMiroBoard ||
                  processing ||
                  miroTemplatePreviewLoading ||
                  (useMiroTemplateMode && (!selectedMiroTemplate || !miroTemplatePreview))
                }
                className="btn btn-primary flex items-center gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                {processing
                  ? 'Importing…'
                  : useMiroTemplateMode
                    ? miroTemplatePreview
                      ? `Import ${miroTemplatePreview.totalItems} ${miroTemplatePreview.totalItems === 1 ? 'note' : 'notes'} ${miroPreviewNotice?.warning ? 'anyway' : 'with zones'}`
                      : 'Import with Zones'
                    : 'Import from Miro'}
              </button>
            )}
            {step === 'configure' && connector === 'notion' && notionConnected && mode === 'import' && (
              <button
                type="button"
                onClick={importFromNotion}
                disabled={!selectedNotionDb || processing || importBlocked}
                title={importBlockedTitle}
                className="btn btn-primary flex items-center gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                {processing ? 'Importing…' : 'Import from Notion'}
              </button>
            )}
            {step === 'configure' && connector === 'linear' && linearConnected && mode === 'import' && (
              <button
                type="button"
                onClick={importFromLinear}
                disabled={(!selectedLinearTeam && !selectedLinearProject) || processing || importBlocked}
                title={importBlockedTitle}
                className="btn btn-primary flex items-center gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                {processing ? 'Importing…' : 'Import from Linear'}
              </button>
            )}
            {step === 'configure' && connector === 'miro' && miroConnected && mode === 'export' && (
              <button
                type="button"
                onClick={exportToMiro}
                disabled={!selectedMiroTemplate || processing}
                className="btn btn-primary flex items-center gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                {processing ? 'Exporting…' : 'Export to Miro'}
              </button>
            )}

            {/* Jira action */}
            {step === 'configure' && connector === 'jira' && jiraConnected && (
              <button
                type="button"
                onClick={mode === 'import' ? importFromJira : exportToJira}
                disabled={!selectedJiraProject || processing || importBlocked}
                title={importBlockedTitle}
                className="btn btn-primary flex items-center gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                {processing ? (mode === 'import' ? 'Importing…' : 'Exporting…') : (mode === 'import' ? 'Import from Jira' : 'Export to Jira')}
              </button>
            )}
          </div>
        )}

        {step === 'done' && (
          <div className="px-6 py-4 border-t border-[var(--rule)] flex justify-end gap-2">
            {mode === 'import' && entityType === 'items' && resultCount > 0 && !importBatch ? (
              <>
                <button type="button" onClick={onClose} className="btn btn-secondary">
                  Done
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate('/prioritization/items');
                  }}
                  className="btn btn-primary"
                >
                  View imported items
                </button>
              </>
            ) : (
              <button type="button" onClick={onClose} className="btn btn-primary">
                Done
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
